import { logger } from "@/lib/logger";

// 缓存管理工具
export interface CacheEntry<T> {
	data: T;
	timestamp: number;
	ttl: number; // Time to live in milliseconds
}

export interface CacheOptions {
	ttl?: number; // Default TTL in milliseconds
	persistent?: boolean; // Whether to persist to localStorage
}

export class CacheManager<T = any> {
	private memoryCache: Map<string, CacheEntry<T>> = new Map();
	private readonly prefix: string;
	private readonly defaultTTL: number;
	private readonly persistent: boolean;
	private readonly log = logger.child({ module: "utils/cache-manager" });

	constructor(prefix: string, options: CacheOptions = {}) {
		this.prefix = prefix;
		this.defaultTTL = options.ttl || 5 * 60 * 1000; // Default 5 minutes
		this.persistent = options.persistent ?? true;

		// Load from localStorage if persistent
		if (this.persistent && typeof window !== "undefined") {
			this.loadFromStorage();
		}
	}

	private getStorageKey(key: string): string {
		return `${this.prefix}_${key}`;
	}

	private loadFromStorage(): void {
		if (typeof window === "undefined") return;

		try {
			const keys = Object.keys(localStorage).filter((k) =>
				k.startsWith(this.prefix),
			);

			for (const storageKey of keys) {
				const data = localStorage.getItem(storageKey);
				if (data) {
					try {
						const entry = JSON.parse(data) as CacheEntry<T>;
						const key = storageKey.replace(`${this.prefix}_`, "");

						// Check if still valid
						if (this.isValid(entry)) {
							this.memoryCache.set(key, entry);
						} else {
							// Clean up expired entries
							localStorage.removeItem(storageKey);
						}
					} catch (e) {
						// Invalid data, remove it
						localStorage.removeItem(storageKey);
					}
				}
			}
		} catch (e) {
			this.log.error("Failed to load cache from storage", {
				event: "cache_load_error",
				error: e,
			});
		}
	}

	private saveToStorage(key: string, entry: CacheEntry<T>): void {
		if (!this.persistent || typeof window === "undefined") return;

		try {
			const storageKey = this.getStorageKey(key);
			localStorage.setItem(storageKey, JSON.stringify(entry));
		} catch (e) {
			this.log.error("Failed to save cache to storage", {
				event: "cache_save_error",
				error: e,
			});
			// Storage might be full, try to clean up old entries
			this.cleanupStorage();
		}
	}

	private removeFromStorage(key: string): void {
		if (!this.persistent || typeof window === "undefined") return;

		try {
			const storageKey = this.getStorageKey(key);
			localStorage.removeItem(storageKey);
		} catch (e) {
			this.log.error("Failed to remove cache from storage", {
				event: "cache_remove_error",
				error: e,
			});
		}
	}

	private cleanupStorage(): void {
		if (typeof window === "undefined") return;

		try {
			const keys = Object.keys(localStorage).filter((k) =>
				k.startsWith(this.prefix),
			);
			const now = Date.now();

			for (const storageKey of keys) {
				const data = localStorage.getItem(storageKey);
				if (data) {
					try {
						const entry = JSON.parse(data) as CacheEntry<T>;
						if (!this.isValid(entry, now)) {
							localStorage.removeItem(storageKey);
						}
					} catch (e) {
						// Invalid data, remove it
						localStorage.removeItem(storageKey);
					}
				}
			}
		} catch (e) {
			this.log.error("Failed to cleanup storage", {
				event: "cache_cleanup_error",
				error: e,
			});
		}
	}

	private isValid(entry: CacheEntry<T>, now?: number): boolean {
		const currentTime = now || Date.now();
		return currentTime - entry.timestamp < entry.ttl;
	}

	set(key: string, data: T, ttl?: number): void {
		const entry: CacheEntry<T> = {
			data,
			timestamp: Date.now(),
			ttl: ttl || this.defaultTTL,
		};

		this.memoryCache.set(key, entry);
		this.saveToStorage(key, entry);
	}

	get(key: string): T | null {
		const entry = this.memoryCache.get(key);

		if (!entry) {
			return null;
		}

		if (!this.isValid(entry)) {
			this.delete(key);
			return null;
		}

		return entry.data;
	}

	has(key: string): boolean {
		const entry = this.memoryCache.get(key);

		if (!entry) {
			return false;
		}

		if (!this.isValid(entry)) {
			this.delete(key);
			return false;
		}

		return true;
	}

	delete(key: string): void {
		this.memoryCache.delete(key);
		this.removeFromStorage(key);
	}

	clear(): void {
		// Clear memory cache
		this.memoryCache.clear();

		// Clear storage
		if (this.persistent && typeof window !== "undefined") {
			try {
				const keys = Object.keys(localStorage).filter((k) =>
					k.startsWith(this.prefix),
				);
				for (const key of keys) {
					localStorage.removeItem(key);
				}
			} catch (e) {
				this.log.error("Failed to clear storage", {
					event: "cache_clear_error",
					error: e,
				});
			}
		}
	}

	// Get cache statistics
	getStats(): {
		size: number;
		entries: {
			key: string;
			timestamp: number;
			ttl: number;
			expired: boolean;
		}[];
	} {
		const now = Date.now();
		const entries: {
			key: string;
			timestamp: number;
			ttl: number;
			expired: boolean;
		}[] = [];

		this.memoryCache.forEach((entry, key) => {
			entries.push({
				key,
				timestamp: entry.timestamp,
				ttl: entry.ttl,
				expired: !this.isValid(entry, now),
			});
		});

		return {
			size: this.memoryCache.size,
			entries,
		};
	}

	// Force refresh: invalidate cache entry
	invalidate(key: string): void {
		const entry = this.memoryCache.get(key);
		if (entry) {
			// Set timestamp to very old to force expiration
			entry.timestamp = 0;
			this.memoryCache.set(key, entry);
			this.saveToStorage(key, entry);
		}
	}

	// Cleanup expired entries
	cleanup(): void {
		const now = Date.now();
		const keysToDelete: string[] = [];

		this.memoryCache.forEach((entry, key) => {
			if (!this.isValid(entry, now)) {
				keysToDelete.push(key);
			}
		});

		for (const key of keysToDelete) {
			this.delete(key);
		}
	}
}

// Singleton instance for VIP cache
export const vipCacheManager = new CacheManager("vip_cache", {
	ttl: 5 * 60 * 1000, // 5 minutes
	persistent: true,
});

// 定期清理过期缓存（每10分钟）
if (typeof window !== "undefined") {
	setInterval(
		() => {
			vipCacheManager.cleanup();
		},
		10 * 60 * 1000,
	);
}
